import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [info, setInfo] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setStatus("expired"); return; }
      attempts++;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setInfo(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[#1B3A6B] mx-auto animate-spin" />
          <h1 className="mt-6 font-heading text-3xl font-bold text-[#1B3A6B]">Confirming your booking…</h1>
          <p className="mt-3 text-slate-600">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 rounded-full bg-[#5BA83F]/15 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-[#3F7A1F]" />
          </div>
          <h1 className="mt-6 font-heading text-3xl md:text-4xl font-bold text-[#1B3A6B]">Booking confirmed!</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We've received your payment of €{(info?.amount_total / 100).toFixed(2)}. Your invoice and booking details are in your account.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/my-account" data-testid="success-account-btn">
              <Button className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full px-6 h-11">View my bookings</Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-full px-6 h-11">Back to home</Button>
            </Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
            <XCircle className="w-8 h-8 text-rose-600" />
          </div>
          <h1 className="mt-6 font-heading text-3xl font-bold text-[#1B3A6B]">Payment not completed</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Something went wrong or the session expired. You can try again.</p>
          <div className="mt-8">
            <Button onClick={() => nav("/book")} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full px-6 h-11">Try again</Button>
          </div>
        </>
      )}
    </div>
  );
}
